import { FileItem } from "@/components/files/FileItem.tsx";
import { InformBox } from "@/components/inform-box/InformBox.tsx";
import { CommentElement } from "@/components/questionnaire/components/CommentElement.tsx";
import { QuestionElementProps } from "@/components/questionnaire/components/element.type.ts";
import { useOptionalQuestionnaireContext } from "@/components/questionnaire/questionnaire-context.ts";
import {
  getFormKey,
  QuestionnaireAnswerType,
  QuestionnaireFormValueTypes,
} from "@/components/questionnaire/questionnaire.types.ts";
import {
  useEmployeeSurveyAnswerFileDelete,
  useEmployeeSurveyAnswerFileUpload,
} from "@/queries/employees/use-employee-survey.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ApiFile } from "@/types/common.types.ts";
import {
  Box,
  ButtonIcon,
  FileDropzone,
  FileDropzoneOnDrop,
  FontAwesomeIcon,
  ProgressBar,
  Stack,
  Text,
  Title,
  HtmlViewer,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

const MAX_FILES = 5;
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

interface PendingFile {
  id: string;
  file: File;
  progress: number;
  error?: string;
}

const getFilesFromFormValue = (value: QuestionnaireAnswerType): ApiFile[] =>
  Array.isArray(value) ? value.filter((item): item is ApiFile => typeof item !== "number") : [];

export const FileElement = ({ element }: QuestionElementProps) => {
  const { t } = useTranslation();
  const { renderFileSize } = useFormatter();
  const { data: employee } = useEmployeeMe();
  const questionnaireContext = useOptionalQuestionnaireContext();
  const questionnaireId = questionnaireContext?.questionnaireId;
  const isAnonymous = questionnaireContext?.isAnonymous ?? false;
  const uploadFile = useEmployeeSurveyAnswerFileUpload();
  const deleteFile = useEmployeeSurveyAnswerFileDelete();
  const {
    getValues,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<QuestionnaireFormValueTypes>();
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [selectionError, setSelectionError] = useState<string>();

  const formKey = getFormKey(element);
  const value = useWatch({ control, name: formKey });
  const settings = element.file_settings;
  if (!settings) return null;
  const files = getFilesFromFormValue(value);
  const uploadingCount = pendingFiles.filter((file) => !file.error).length;
  const filesCount = files.length + uploadingCount;
  const fieldError = errors[formKey]?.message as string | undefined;

  const updateFiles = (nextFiles: ApiFile[]) => {
    setValue(formKey, nextFiles, { shouldDirty: true, shouldValidate: true });
  };

  const handleUpload = async (pendingFile: PendingFile) => {
    if (!employee || questionnaireId === undefined) return;

    try {
      const uploadedFile = await uploadFile.mutateAsync({
        employeeId: employee.id,
        surveyId: questionnaireId,
        formElementId: element.id,
        file: pendingFile.file,
        onUploadProgress: ({ loaded, total }) => {
          const progress = total ? Math.round((loaded / total) * 100) : 0;
          setPendingFiles((current) =>
            current.map((item) => (item.id === pendingFile.id ? { ...item, progress } : item))
          );
        },
      });

      const currentFilesValue = getValues(formKey);
      const currentFiles = getFilesFromFormValue(currentFilesValue);
      updateFiles([...currentFiles, uploadedFile]);
      setPendingFiles((current) => current.filter((item) => item.id !== pendingFile.id));
    } catch {
      setPendingFiles((current) =>
        current.map((item) =>
          item.id === pendingFile.id
            ? { ...item, error: t("surveys:file_upload_server_error") }
            : item
        )
      );
    }
  };

  const handleSelect = (selectedFiles: File[]) => {
    setSelectionError(undefined);
    const availableCount = MAX_FILES - filesCount;

    if (selectedFiles.length > availableCount) {
      setSelectionError(t("surveys:file_upload_count_error", { count: MAX_FILES }));
    }

    selectedFiles.slice(0, Math.max(availableCount, 0)).forEach((file) => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setPendingFiles((current) => [
          ...current,
          {
            id: crypto.randomUUID(),
            file,
            progress: 0,
            error: t("surveys:file_upload_size_error", { size: 20 }),
          },
        ]);
        return;
      }

      const pendingFile: PendingFile = {
        id: crypto.randomUUID(),
        file,
        progress: 0,
      };
      setPendingFiles((current) => [...current, pendingFile]);
      void handleUpload(pendingFile);
    });
  };

  const handleDrop: FileDropzoneOnDrop = (acceptedFiles, fileRejections) => {
    fileRejections.forEach(({ file }) => {
      setPendingFiles((current) => [
        ...current,
        {
          id: crypto.randomUUID(),
          file,
          progress: 0,
          error: t("surveys:file_upload_size_error", { size: 20 }),
        },
      ]);
    });
    handleSelect(acceptedFiles);
  };

  const handleRemove = async (file: ApiFile) => {
    if (!employee || questionnaireId === undefined) return;

    try {
      await deleteFile.mutateAsync({
        employeeId: employee.id,
        surveyId: questionnaireId,
        formElementId: element.id,
        fileUuid: file.uuid,
      });
    } catch {
      return;
    }
    const currentFilesValue = getValues(formKey);
    const currentFiles = getFilesFromFormValue(currentFilesValue);
    updateFiles(currentFiles.filter((item) => item.uuid !== file.uuid));
  };

  return (
    <Stack gap="lg" data-error={fieldError || undefined}>
      {(settings.title || settings.description) && (
        <Stack gap="md">
          <Title header={settings.title || undefined} required={settings.is_required} size="md" />
          {settings.description && <HtmlViewer content={settings.description} />}
        </Stack>
      )}

      {isAnonymous && (
        <InformBox
          emoji=":warning:"
          title={t("surveys:file_answer_anonymous_warning_title")}
          description={t("surveys:file_answer_anonymous_warning_description")}
        />
      )}

      <Stack gap="sm">
        <Stack direction="horizontal" justify="between" items="center">
          <Text className="font-semibold">{t("surveys:file_upload_title")}</Text>
          <Text className="text-secondary">{`${filesCount} / ${MAX_FILES}`}</Text>
        </Stack>

        {files.map((file) => (
          <FileItem
            key={file.uuid}
            file={file}
            removeTitle={t("surveys:file_upload_remove")}
            onRemove={() => void handleRemove(file)}
          />
        ))}

        {pendingFiles.map((pendingFile) => (
          <Box key={pendingFile.id} className="rounded-xs bg-dark/5 px-2 py-2">
            <Stack gap="xs">
              <Stack direction="horizontal" justify="between" items="center" gap="sm">
                <Stack className="min-w-0" gap="xs">
                  <Text className="truncate">{pendingFile.file.name}</Text>
                  <Text className="text-secondary">{renderFileSize(pendingFile.file.size)}</Text>
                </Stack>
                {pendingFile.error && (
                  <ButtonIcon
                    variant="plain"
                    size="sm"
                    title={t("surveys:file_upload_remove")}
                    onClick={() =>
                      setPendingFiles((current) =>
                        current.filter((item) => item.id !== pendingFile.id)
                      )
                    }
                    icon={<FontAwesomeIcon icon="fa-light fa-times" />}
                  />
                )}
              </Stack>
              {pendingFile.error ? (
                <Text className="text-danger">{pendingFile.error}</Text>
              ) : (
                <ProgressBar
                  completed={pendingFile.progress}
                  total={100}
                  tooltip={`${pendingFile.progress}%`}
                />
              )}
            </Stack>
          </Box>
        ))}

        <FileDropzone
          onDrop={handleDrop}
          multiple
          maxSize={MAX_FILE_SIZE_BYTES}
          disabled={!employee || questionnaireId === undefined || filesCount >= MAX_FILES}
          ctaActionText={t("surveys:file_upload_button")}
          description={t("surveys:file_upload_limits", { count: MAX_FILES, size: 20 })}
          activeDescription={t("common:upload_file_drag_and_drop")}
          rejectDescription={t("surveys:file_upload_size_error", { size: 20 })}
          error={selectionError || fieldError}
        />
      </Stack>

      {settings.is_comment_enabled && <CommentElement element={element} />}
    </Stack>
  );
};
